import { zodResolver } from '@hookform/resolvers/zod';
import {
  type InviteAdminInput,
  type InviteCreatedResponse,
  InviteAdminInput as InviteAdminInputSchema,
  InviteCreatedResponse as InviteCreatedResponseSchema,
} from '@uit/shared/schemas/admin';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AdminsSection } from '../components/settings/AdminsSection.js';
import { FeatureFlagsSection } from '../components/settings/FeatureFlagsSection.js';
import { OnboardingEmailsSection } from '../components/settings/OnboardingEmailsSection.js';
import { PolicyDocumentsSection } from '../components/settings/PolicyDocumentsSection.js';
import { QuickbooksSection } from '../components/settings/QuickbooksSection.js';
import { RcfEmailSection } from '../components/settings/RcfEmailSection.js';
import { RcfReferenceSections } from '../components/settings/RcfReferenceSections.js';
import { ReferrerVendorsSection } from '../components/settings/ReferrerVendorsSection.js';
import { UitSignerEmailSection } from '../components/settings/UitSignerEmailSection.js';
import { apiMutation } from '../lib/api.js';
import { useFeatureFlag } from '../lib/feature-flags.js';
import s from './SettingsPage.module.scss';

export function SettingsPage() {
  // RCF reference + email settings are gated by the rcf feature flag (T-152).
  const rcfEnabled = useFeatureFlag('rcf');
  const [success, setSuccess] = useState<string | null>(null);
  // Partial success (T-181): the invite row exists but the email never left.
  const [warning, setWarning] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteAdminInput>({
    resolver: zodResolver(InviteAdminInputSchema),
    defaultValues: { email: '', name: '', role: 'onboarding_only' },
  });

  const onSubmit = async (data: InviteAdminInput): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    setSuccess(null);
    setWarning(null);
    try {
      const res = await apiMutation<InviteAdminInput, InviteCreatedResponse>(
        '/api/admins/invite',
        data,
        { schema: InviteCreatedResponseSchema },
      );
      if (res.email_outcome === 'sent') {
        setSuccess(`Invite sent to ${data.email}. In dev, check the API logs for the accept URL.`);
      } else {
        setWarning(
          res.email_outcome === 'config_error'
            ? `The invite for ${data.email} was created, but the email could not be sent — the ` +
                `email service is misconfigured, so every email will fail until it's fixed. ` +
                `Once it is, use Resend in the Pending invites list below.`
            : `The invite for ${data.email} was created, but the email failed to send. ` +
                `Use Resend in the Pending invites list below.`,
        );
      }
      reset({ email: '', name: '', role: 'onboarding_only' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('409')) {
        setSubmitError('An admin or pending invite already exists for that email.');
      } else if (message.includes('403')) {
        setSubmitError('You do not have permission to invite admins.');
      } else {
        setSubmitError('Could not send invite. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <h1 className={s.title}>Settings</h1>
      <p className={s.subtitle}>Administer the portal.</p>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Invite admin</h2>
        <p className={s.sectionSubtitle}>
          The invitee receives a one-time link valid for 7 days. They set their own password.
        </p>

        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <div className={s.field}>
            <label className={s.label} htmlFor="invite-email">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              autoComplete="off"
              className={s.input}
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && <div className={s.fieldError}>{errors.email.message}</div>}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="invite-name">
              Name
            </label>
            <input
              id="invite-name"
              type="text"
              className={s.input}
              aria-invalid={!!errors.name}
              {...register('name')}
            />
            {errors.name && <div className={s.fieldError}>{errors.name.message}</div>}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="invite-role">
              Role
            </label>
            <select
              id="invite-role"
              className={s.select}
              aria-invalid={!!errors.role}
              {...register('role')}
            >
              <option value="onboarding_only">Onboarding only</option>
              <option value="admin">Admin (full access)</option>
            </select>
            {errors.role && <div className={s.fieldError}>{errors.role.message}</div>}
          </div>

          <button type="submit" className={s.button} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send invite'}
          </button>

          {success && (
            <div className={s.success} role="status">
              {success}
            </div>
          )}
          {warning && (
            <div className={s.warning} role="alert">
              {warning}
            </div>
          )}
          {submitError && (
            <div className={s.error} role="alert">
              {submitError}
            </div>
          )}
        </form>
      </section>

      <FeatureFlagsSection />

      <UitSignerEmailSection />

      <AdminsSection />

      <ReferrerVendorsSection />

      <QuickbooksSection />

      <PolicyDocumentsSection />

      <OnboardingEmailsSection />

      {rcfEnabled && (
        <>
          <RcfReferenceSections />
          <RcfEmailSection />
        </>
      )}
    </div>
  );
}
